import { AxiosError } from "axios";
import {
  QueryObserverSuccessResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiPaths, RESPONSE_STATUS } from "@src/constants";
import { Clients } from "@src/api/v1/Clients";
import {
  GetClientsParams,
  ModelsQuestionsQuestionList,
  RequestClientCreateRequest,
  RequestClientUpdateRequest,
  ResponseHostClientWithRelationshipResponse,
} from "@src/api/v1/data-contracts";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { useAuthentication } from "@src/components/wrappers/AuthenticationProvider";
import { fiveMinutes } from "./staletimes";
import { queryIdentifiers } from "../utils/constants";

export const useClientsQuery = (queryParams: GetClientsParams = {}) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [queryIdentifiers.clients, queryParams],
    queryFn: () => new Clients(OpenUpHttpClient).getClients(queryParams),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });
};
export const useMyClientsQuery = (
  queryParams: {
    page: number;
    search: string;
  } = { page: 0, search: "" },
) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [queryIdentifiers.myClients, queryParams],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getPsychologistClients({
        page: queryParams.page,
        search: queryParams.search,
      }),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });
};

export const useClientQuery = (clientId: string | undefined) => {
  const { isLoggedIn } = useAuthentication();
  return useQuery({
    queryKey: [queryIdentifiers.clients, clientId],
    queryFn: () => new Clients(OpenUpHttpClient).getClientById(clientId!),
    staleTime: fiveMinutes,
    enabled: !!clientId && isLoggedIn,
    suspense: false,
  }) as QueryObserverSuccessResult<ResponseHostClientWithRelationshipResponse>;
};

export const useClientTagsQuery = (clientId: string) => {
  const { isLoggedIn } = useAuthentication();
  return useQuery({
    queryKey: [queryIdentifiers.clients, clientId, "tags"],
    queryFn: () => new Clients(OpenUpHttpClient).getClientTags(clientId),
    staleTime: fiveMinutes,
    enabled: isLoggedIn,
  });
};

export const useClientEmotionsOverviewQuery = (
  userId: string,
  questionListName: string,
) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      userId,
      queryIdentifiers.questionListAnswers,
      questionListName,
    ],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getUserQuestionListAnswers(
        userId,
        questionListName,
      ),
    staleTime: fiveMinutes,
    suspense: false,
  }) as QueryObserverSuccessResult<ModelsQuestionsQuestionList[]>;

export const useClientCheckinOverviewQuery = (userId: string) =>
  useQuery({
    queryKey: [queryIdentifiers.clients, userId, queryIdentifiers.themes],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getClientVitalityCheckinResultsSummary(
        userId,
      ),
    staleTime: fiveMinutes,
  });

export const useClientMedicalCheckinOverviewQuery = (userId: string) => {
  const queryClient = useQueryClient();
  const key = [apiPaths.clients, userId, "medical-themes"];
  const result = useQuery({
    queryKey: key,
    queryFn: () =>
      new Clients(OpenUpHttpClient).getUserMedicalCheckinResults(userId),
  });
  if (
    (result.error as AxiosError)?.response?.status === RESPONSE_STATUS.NOT_FOUND
  ) {
    queryClient.setQueryData(key, []);
  }
  return result;
};

export const useClientThemeAnswersQuery = (userId: string, formName: string) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      userId,
      queryIdentifiers.themes,
      formName,
    ],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getClientVitalityCheckinResultsForTheme(
        userId,
        formName,
      ),
    staleTime: fiveMinutes,
  });

export const useClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      user: { id: string } & (
        | RequestClientCreateRequest
        | RequestClientUpdateRequest
      ),
    ) =>
      user.id
        ? new Clients(OpenUpHttpClient).updateClient(user.id, user)
        : new Clients(OpenUpHttpClient).createClient(user),
    onSuccess: (data) => {
      queryClient.setQueryData([queryIdentifiers.clients, data.id], data);
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.themes],
      });
    },
  });
};

export const useClientAnonymizeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      new Clients(OpenUpHttpClient).anonymizeClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
    },
  });
};

export const useClientDossierNoteQuery = (clientId: string) =>
  useQuery({
    queryKey: [
      queryIdentifiers.clients,
      clientId,
      queryIdentifiers.dossierNote,
    ],
    queryFn: () => new Clients(OpenUpHttpClient).getClientDossierNote(clientId),
    staleTime: fiveMinutes,
  });

export const useClientDossierNoteMutation = (clientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (value: string) =>
      new Clients(OpenUpHttpClient).updateClientDossierNote(clientId, {
        value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [apiPaths.clients, clientId, queryIdentifiers.dossierNote],
      });
    },
  });
};

export const useClientUnlockMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => new Clients(OpenUpHttpClient).unlockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

export const useRemove2FAMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      new Clients(OpenUpHttpClient).removeTwoFactorFromUser(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.clients],
      });
    },
  });
};
